<template>
  <div class="BeneficiariesForm">
    <h2 class="gl_form-title small mb-5">
      {{ t('copy_life_beneficiaries-form_title') }}
    </h2>

    <BaseCheckBox
      v-if="beneficiaries.length > 1"
      :key="Number(equalValues)"
      id="equal-values"
      v-model="equalValues"
      :label="t('copy_life_beneficiaries-form_check')"
    />

    <div
      class="beneficiary mb-3"
      v-for="(beneficiary, i) in beneficiaries"
      :key="i"
    >
      <UserCardForm
        :title="`${beneficiary.name} (${
          beneficiary.documentType === 'MENOR'
            ? beneficiary.documentTypeText
            : beneficiary.documentNumber
        })`"
      />

      <AdvantageSliderComponent
        :id="`beneficiary-slider-${i}`"
        v-model="distributionPercentages[i]"
        @update:model-value="onUpdateDistribution"
        :min="MIN"
        :max="MAX"
        :step="1"
        units="%"
      />
    </div>

    <p class="gl__subtitle-light">
      {{ t('copy_life_beneficiaries_validation-message') }}
    </p>

    <div
      ref="hiddenFieldWrapper"
      style="display: none"
      aria-hidden="true"
    >
      <v-text-field
        aria-hidden="true"
        :aria-describedby="undefined"
        tabindex="-1"
        v-model="total"
        :rules="[validateTotal]"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import BaseCheckBox from '@/modules/core/components/ui/BaseCheckBox.vue'
  import AdvantageSliderComponent from '@/modules/core/components/utils/AdvantageSliderComponent.vue'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import UserCardForm from '@/modules/health/hiring/components/UserCardForm.vue'
  import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
  import type LifeHiringAnswers from '@/modules/life/hiring/models/APP/LifeHiringAnswers'
  import { computed, inject, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import LifeBeneficiary from '../../models/APP/LifeBeneficiary'

  const { t } = useI18n()
  const loadingStore = useLoadingStore()

  const validateForm = inject<() => Promise<boolean>>('validateForm') ?? (async () => true)

  defineExpose({ triggerSubmit })

  const MAX = 100
  const MIN = 1

  interface PropsInterface {
    nextForm: String[]
    answers: LifeHiringAnswers
    configurations: LifeConfigurations
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const equalValues = ref(false)
  const fromCheckButton = ref(false)
  const beneficiaries = ref<LifeBeneficiary[]>(getInitBeneficiaries())

  const distributionPercentages = ref<number[]>(getInitDistributionPercentages())

  const total = computed(() => distributionPercentages.value.reduce((acc, d) => acc + d, 0))

  watch(total, validateForm)

  watch(equalValues, (value: boolean) => {
    updateDistributions(value)

    //* Avoid unchecking the check when the user matches values with the check button
    fromCheckButton.value = true
    setTimeout(() => (fromCheckButton.value = false), 200)
  })

  function validateTotal(value: number) {
    return MAX === value
  }

  function onUpdateDistribution() {
    //* Uncheck the check box when the user changes the values of the slider
    if (!fromCheckButton.value) {
      equalValues.value = false
    }
  }

  function updateDistributions(check: boolean) {
    equalValues.value = check

    if (!equalValues.value) return

    const valuePerBeneficiary = Math.floor(MAX / beneficiaries.value.length)
    let rest = MAX - valuePerBeneficiary * beneficiaries.value.length

    distributionPercentages.value = distributionPercentages.value.map(() => {
      const value = valuePerBeneficiary + Number(rest > 0)
      rest = Math.max(--rest, 0)

      return value
    })
  }

  function getInitDistributionPercentages() {
    if (beneficiaries.value.length === 1) {
      return [MAX]
    }

    return beneficiaries.value.map((b) => b.distributionPercentage)
  }

  function getInitBeneficiaries() {
    const insured = props.answers.insureds[0]
    return insured.beneficiaries ?? []
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    beneficiaries.value.forEach((b, i) => {
      b.distributionPercentage = distributionPercentages.value[i]
    })

    const insureds = props.answers.insureds
    insureds[0].beneficiaries = beneficiaries.value
    insureds[0].availableToSave = true
    const answers = { insureds }

    return { page, answers }
  }

  async function beforeMount() {
    try {
      loadingStore.startLoading()
    } catch (error) {
      console.error(error)
    } finally {
      loadingStore.stopLoading()
    }
  }

  const hiddenFieldWrapper = ref<HTMLElement | null>(null)
  let hiddenObserver: MutationObserver | null = null

  onMounted(() => {
    validateForm()
    hiddenObserver = startDuplicatedLabelControl(hiddenFieldWrapper.value, {
      labelOrphanedInputs: true
    })
  })
  onUnmounted(() => endDuplicatedLabelControl(hiddenObserver))
  onBeforeMount(beforeMount)
</script>
